import { BrowsePrimaryControlsStripExpansionPushButtons } from "@-/dev.improve.haniverse.routes.nav.app.browse._components.primary-controls-strip-expansion-push-buttons";
import { Progress } from "@-/dev.improve.haniverse.components.ui.progress";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useLevels } from "@-/dev.improve.haniverse.routes.nav.app._lib.level";
import { useBrowseLevelsStudyGraphemesQuery } from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.levels.navigator";
import { View } from "@-/com.npmjs.react-native";
import plurCount from "@-/com.npmjs.plur-count";
import { useMemo } from "react";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export function BrowseLevelsPrimaryControlsStripExpansionContent() {
  // Progressive: the header commits immediately and fills in once the study data settles, rather
  // than reporting "0 levels completed" for a query that simply hasn't answered yet.
  const studyGraphemesQuery = useBrowseLevelsStudyGraphemesQuery();

  return (
    <View className="w-full px-3 pt-0.5 pb-1">
      {studyGraphemesQuery.data === undefined ? (
        <BrowseLevelsProgressPlaceholder
          message={studyGraphemesQuery.isError ? "Couldn't load progress" : "Loading progress…"}
        />
      ) : (
        <BrowseLevelsProgressSummary addedCharacterSet={studyGraphemesQuery.data} />
      )}
      <BrowsePrimaryControlsStripExpansionPushButtons />
    </View>
  );
}

function BrowseLevelsProgressSummary({
  addedCharacterSet,
}: {
  addedCharacterSet: ReadonlySet<string>;
}) {
  const levels = useLevels();

  const { completedLevelCount, learnedCharacterCount, totalLevelCount } = useMemo(() => {
    let completed = 0;
    let total = 0;
    const learnedCharacters = new Set<string>();
    for (const level of levels) {
      if (hasLength(level.characters, 0)) {
        continue;
      }
      total++;
      const isCompleted = level.characters.every((character) => addedCharacterSet.has(character));
      if (isCompleted) {
        completed++;
      }
      for (const character of level.characters) {
        if (addedCharacterSet.has(character)) {
          learnedCharacters.add(character);
        }
      }
    }
    return {
      completedLevelCount: completed,
      learnedCharacterCount: learnedCharacters.size,
      totalLevelCount: total,
    };
  }, [addedCharacterSet, levels]);

  const levelProgressPercent =
    totalLevelCount === 0 ? 0 : (completedLevelCount / totalLevelCount) * 100;

  return (
    <>
      <Progress
        className="h-1.5 rounded-full bg-muted"
        indicatorClassName="rounded-full bg-foreground/70"
        value={levelProgressPercent}
      />
      <Text className="mt-1 text-center font-sans text-[11px] text-muted-foreground">
        {plurCount(completedLevelCount, "level")} completed ·{" "}
        {plurCount(learnedCharacterCount, "character")} learned
      </Text>
    </>
  );
}

/** Same footprint as the summary, so the header doesn't shift when the study data arrives. */
function BrowseLevelsProgressPlaceholder({ message }: { message: string }) {
  return (
    <>
      <Progress
        className="h-1.5 rounded-full bg-muted"
        indicatorClassName="rounded-full bg-foreground/70"
        value={0}
      />
      <Text className="mt-1 text-center font-sans text-[11px] text-muted-foreground">
        {message}
      </Text>
    </>
  );
}
